import { useEffect, useState } from "react";
import {
  ConsultantLeadStatus,
  ConsultantLeadStatusCustom,
  useUntagConsultantClearBAMutation,
  useUntagConsultantGetDataLazyQuery
} from "@bonliva-crm/graphql";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";

type Props = {
  open: boolean;
  consultantId: string | null;
  questionText: string;
  // Fired after the user successfully untagged themselves (Save → mutation ok).
  // Parent should treat the card as removed from this board.
  onConfirm: () => void;
  // Fired when the user dismisses the dialog without untagging (Cancel button,
  // backdrop click, ESC). Parent should NOT remove the card — see CRMDEV-6793.
  onCancel: () => void;
  onError: () => void;
};

export function UntagConsultantDialog(props: Props) {
  const snack = useSnackbarsPipeline();
  const [leadStatus, setLeadStatus] = useState<ConsultantLeadStatusCustom | null>(null);
  const [showLeadSelect, setShowLeadSelect] = useState(false);

  const [load, { loading }] = useUntagConsultantGetDataLazyQuery({
    onCompleted: (data) => {
      const status = data.consultantById?.leadStatus?.status;
      if (status === ConsultantLeadStatus.Assigned) {
        setShowLeadSelect(true);
      } else {
        setShowLeadSelect(false);
      }
    }
  });

  const [clearBA, clearBAMutation] = useUntagConsultantClearBAMutation();

  useEffect(() => {
    if (props.open && props.consultantId) {
      setLeadStatus(null);
      setShowLeadSelect(false);
      load({
        variables: {
          consultantId: props.consultantId
        }
      });
    }
  }, [props.open, props.consultantId, load]);

  const submit = () => {
    clearBA({
      variables: {
        consultantId: props.consultantId,
        status: leadStatus as any as ConsultantLeadStatus
      }
    })
      .then(() => {
        snack.showSuccess("Konsulten är nu avtaggad");
        props.onConfirm();
      })
      .catch((e) => {
        snack.showError("Något gick fel");
        console.error(e);
        props.onError();
      });
  };

  const isValid = showLeadSelect ? !!leadStatus : true;

  const canSubmit = !loading && !clearBAMutation.loading && isValid;

  if (loading) {
    return (
      <Dialog open={props.open}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DialogTitle>Avtagga konsult</DialogTitle>

      <DialogContent>
        <Box>
          <Typography variant="body1">{props.questionText}</Typography>
        </Box>

        {showLeadSelect && (
          <Box pt={3}>
            <Box>
              <Typography variant="body1">Välj ny leadstatus för konsulten:</Typography>
            </Box>

            <Box pt={1}>
              <RadioGroup
                aria-label="gender"
                name="leadStatus"
                value={leadStatus}
                onChange={(_, v) => setLeadStatus(v as ConsultantLeadStatusCustom)}
              >
                {Object.values(ConsultantLeadStatusCustom).map((status) => (
                  <FormControlLabel
                    key={status}
                    value={status}
                    control={<Radio />}
                    label={locale.consultantLeadStatus[status]}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="primary" onClick={() => props.onCancel()}>
          {locale.Cancel}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => submit()}
          disabled={!canSubmit}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
